import {
	BrokersSocietiesWithUsers,
	ClientSociety,
	ClientUser,
	ConstructionSiteFullDetails,
	GFAFullDetails,
	PAGES,
} from "@dune/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { getChangedFieldsObject } from "@dune/utils";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { constructionSitesQueryKeys } from "../../../api/query-keys/constructionSites.query-keys";
import {
	createConstructionSiteClient,
	updateConstructionSiteDetails,
	useSaveConstructionSiteDetails,
	useValidateConstructionSiteDetails,
} from "../../../api/mutations/constructionSite.mutations";
import {
	useSaveGFAAutorisationsAndReferences,
	useSaveGFAFinancings,
	useSaveGFAShareholdersAndExperiences,
} from "../../../api/mutations/gfa.mutations";
import { useGetAllBrokersSocieties } from "../../../api/queries/societies.queries";
import { useMe } from "../../../api/queries/users.queries";
import {
	ConstructionSiteDetailsFormData,
	defaultActionValues,
	useConstructionSiteDetailsForm,
} from "../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import {
	CreateConstructionSiteClientFormData,
	useCreateConstructionSiteForm,
} from "../../../component/brokers/constructionSites/useCreateConstructionSiteForm";
import {
	GFAAutorisationsAndReferencesFormData,
	useGFAAutorisationsAndReferencesForm,
} from "../../../component/gfa/useGFAAutorisationsAndReferencesForm";
import {
	GFAFinancingsFormData,
	useGFAFinancingsForm,
} from "../../../component/gfa/useGFAFinancingsForm";
import {
	GFAShareholdersAndExperiencesFormData,
	useGFAShareholdersAndExperiencesForm,
} from "../../../component/gfa/useGFAShareholdersAndExperiencesForm";
import { useQueryState } from "nuqs";
import { BaseProjectForm } from "./BaseProjectForm";
import { SelectedBrokerSocietyProvider } from "../../../contexts/SelectedBrokerSocietyContext";
import { RoleEnum } from "../../../../../../packages/vars/src";

export type ProjectType = "construction-site" | "gfa";

type CommonCreateProjectProps = {
	projectId: string | undefined;
} & (
	| {
			projectType: "construction-site";
			constructionSiteFullDetails: ConstructionSiteFullDetails | undefined;
	  }
	| {
			projectType: "gfa";
			GFAFullDetails: GFAFullDetails | undefined;
	  }
);

export type SelectedClient =
	| Pick<
			ClientUser,
			"id" | "email" | "firstname" | "lastname" | "mobilePhone" | "address"
	  >[]
	| Pick<ClientSociety, "id" | "siren" | "denomination" | "headOfficeAddress">
	| null
	| undefined;

export const CommonProjectForm = ({
	projectId,
	...otherProps
}: CommonCreateProjectProps) => {
	const navigate = useNavigate();
	const { me } = useMe();
	const hasInitializedExistingProjectRef = useRef(false);

	// ===== COMPUTED VALUES (Early) =====
	const canSelectBrokerSociety = useMemo(
		() => me?.role === RoleEnum.DUNE,
		[me?.role],
	);

	// ===== STATE MANAGEMENT =====
	const [activeStepIndex, setActiveStepIndex] = useQueryState("step", {
		defaultValue: 1,
		parse: (value) => {
			const parsed = Number(value);
			if (
				Number.isNaN(parsed) ||
				parsed < 1 ||
				(otherProps.projectType === "construction-site" && parsed > 3) ||
				(otherProps.projectType === "gfa" && parsed > 6)
			) {
				return 1;
			}
			return parsed;
		},
		serialize: (value) => String(value),
	});
	const [selectedBrokerSociety, setSelectedBrokerSociety] =
		useState<BrokersSocietiesWithUsers | null>();
	const [selectedClient, setSelectedClient] = useState<SelectedClient>();
	const [selectedMOAClient, setSelectedMOAClient] = useState<
		ClientUser | ClientSociety | null
	>();
	const [canSave, setCanSave] = useState(true);
	const [searchBroker, setSearchBroker] = useState<string | undefined>(
		undefined,
	);

	// ===== COMPUTED VALUES =====
	const currentProject =
		otherProps.projectType === "gfa"
			? otherProps.GFAFullDetails
			: otherProps.constructionSiteFullDetails;
	const projectType = otherProps.projectType;
	const GFAFullDetails =
		otherProps.projectType === "gfa" ? otherProps.GFAFullDetails : undefined;

	const { data: brokersSocieties } = useGetAllBrokersSocieties(
		{
			search:
				currentProject?.brokerSociety?.managingBrokerSociety?.denomination ??
				currentProject?.brokerSociety?.denomination ??
				searchBroker,
		},
		{
			enabled: canSelectBrokerSociety,
		},
	);

	const initialClientSocietyId = useMemo(() => {
		const clientDetails =
			currentProject?.constructionSiteClientDetails ?? undefined;
		if (clientDetails?.clientType !== "SOCIETY") return null;
		return clientDetails.clientSociety?.id ?? null;
	}, [currentProject]);

	const currentUserIsSyndic = useMemo(
		() => me?.role === RoleEnum.SYNDIC,
		[me?.role],
	);

	const brokerSocietyOptions = useMemo(
		() =>
			brokersSocieties?.data?.map((brokerSociety) => ({
				label: brokerSociety.denomination,
				value: brokerSociety.id.toString(),
			})) ?? [],
		[brokersSocieties],
	);

	const stepperSteps = useMemo(() => {
		if (projectType === "construction-site") {
			return [
				{ step: 1, title: "Mon client" },
				{ step: 2, title: "Opération à assurer" },
				{ step: 3, title: "Indications tarifaires" },
			];
		}
		return [
			{ step: 1, title: "Mon client" },
			{ step: 2, title: "Opération à assurer" },
			{ step: 3, title: "Financement de l'opération" },
			{ step: 4, title: "Actionnariat & Expérience" },
			{ step: 5, title: "Autorisations" },
			{ step: 6, title: "Pièces justificatives" },
		];
	}, [projectType]);

	const pageTitle = useMemo(() => {
		return projectType === "construction-site"
			? "Nouveau projet - Chantier de construction"
			: "Nouveau projet GFA";
	}, [projectType]);

	// ===== FORMS =====
	const clientForm = useCreateConstructionSiteForm(currentProject);
	const { form: detailsForm, defaultValues: detailsDefaultValues } =
		useConstructionSiteDetailsForm(currentProject ?? null);

	// GFA-specific forms
	const { form: financingsForm, defaultValues: financingsDefaultValues } =
		useGFAFinancingsForm(GFAFullDetails ?? null);
	const {
		form: shareholdersAndExperiencesForm,
		defaultValues: shareholdersAndExperiencesDefaultValues,
	} = useGFAShareholdersAndExperiencesForm(
		GFAFullDetails ?? null,
		GFAFullDetails?.constructionSiteClientDetails?.moaClientType ?? null,
	);
	const {
		form: autorisationsAndReferencesForm,
		defaultValues: autorisationsAndReferencesDefaultValues,
	} = useGFAAutorisationsAndReferencesForm(GFAFullDetails ?? null);

	// ===== FORM HANDLERS =====
	const {
		handleSubmit: handleSubmitClient,
		getValues: getClientValues,
		setValue: setClientValues,
		watch: watchClientValues,
	} = clientForm;
	const {
		handleSubmit: handleSubmitDetails,
		setValue: setDetailsValues,
		watch: watchDetailsValues,
	} = detailsForm;
	const actionsValue = watchDetailsValues("actions");
	const clientTypeValue = getClientValues("clientType");
	const clientSocietyValue = watchClientValues("clientSociety");

	// ===== COMPUTED VALUES (Form-dependent) =====
	const currentUserIsClient = useMemo(() => {
		return currentUserIsSyndic && actionsValue?.buildingType === "COMMON_PARTS";
	}, [currentUserIsSyndic, actionsValue?.buildingType]);

	const clientIsSyndic = Boolean(
		clientTypeValue === "SOCIETY" && clientSocietyValue?.isSyndic,
	);
	// ===== HELPER FUNCTIONS =====
	const getCurrentUserSociety = useCallback(() => {
		if (!me) return null;
		return me.society;
	}, [me]);

	// ===== EFFECTS =====
	// Initialize data from existing project
	useEffect(() => {
		if (
			!currentProject ||
			hasInitializedExistingProjectRef.current ||
			(canSelectBrokerSociety && !brokersSocieties)
		) {
			return;
		}

		setSelectedClient(
			currentProject.constructionSiteClientDetails?.clientType === "SOCIETY"
				? currentProject.constructionSiteClientDetails.clientSociety
				: currentProject.constructionSiteClientDetails?.constructionSiteClientContacts?.map(
						(contact) => contact.constructionSiteContact,
					),
		);
		setSelectedMOAClient(
			currentProject.constructionSiteClientDetails?.moaClientType === "SOCIETY"
				? currentProject.constructionSiteClientDetails?.moaClientSociety
				: currentProject.constructionSiteClientDetails?.moaClientContact,
		);
		setSelectedBrokerSociety(
			brokersSocieties?.data?.find((brokerSociety) =>
				currentProject.brokerSociety.managingBrokerSociety
					? currentProject.brokerSociety.managingBrokerSociety.id ===
						brokerSociety.id
					: currentProject.brokerSociety.id === brokerSociety.id,
			) ?? null,
		);
		setCanSave(
			!currentProject.isSkipped && currentProject.status !== "VALIDATED",
		);

		hasInitializedExistingProjectRef.current = true;
	}, [currentProject, brokersSocieties, canSelectBrokerSociety]);
	// Syndic-specific logic - only for user changes, not during initialization from existing project
	useEffect(() => {
		if (!currentUserIsSyndic || activeStepIndex !== 1) return;

		if (actionsValue.buildingType === undefined) {
			setDetailsValues("actions", {
				...defaultActionValues,
				types: ["SYNDIC"],
				buildingType: "COMMON_PARTS",
			});
		}

		if (actionsValue.buildingType === "COMMON_PARTS") {
			const currentUserSocietyClient = getCurrentUserSociety();
			if (currentUserSocietyClient) {
				setSelectedClient(currentUserSocietyClient);
				setClientValues("clientType", "SOCIETY");
				setClientValues("clientSocietyId", currentUserSocietyClient.id);
				setClientValues("clientSociety", {
					...currentUserSocietyClient,
					siren: currentUserSocietyClient.siren,
					streetNumber:
						currentUserSocietyClient.headOfficeAddress?.streetNumber ?? "",
					streetName:
						currentUserSocietyClient.headOfficeAddress?.streetName ?? "",
					postalCode:
						currentUserSocietyClient.headOfficeAddress?.postalCode ?? "",
					city: currentUserSocietyClient.headOfficeAddress?.city ?? "",
					isSyndic: true,
					hasUserAccess: currentUserSocietyClient.syndicUserAccessToPlatform
						? "true"
						: "false",
					userAccess: currentUserSocietyClient.syndicUserAccessToPlatform
						? {
								firstname:
									currentUserSocietyClient.syndicUserAccessToPlatform.firstname,
								lastname:
									currentUserSocietyClient.syndicUserAccessToPlatform.lastname,
								email:
									currentUserSocietyClient.syndicUserAccessToPlatform.email ??
									"",
							}
						: undefined,
					hasBrokerageFees: currentUserSocietyClient.brokerageFees
						? "true"
						: "false",

					brokerageFees:
						currentUserSocietyClient.brokerageFees?.toString() ?? undefined,
				});
				setClientValues("clientIsMOA", "true");
				setClientValues("moaClientType", "SOCIETY");
				setClientValues("moaClientSociety", {
					...currentUserSocietyClient,
					siren: currentUserSocietyClient.siren,
					streetNumber:
						currentUserSocietyClient.headOfficeAddress?.streetNumber ?? "",
					streetName:
						currentUserSocietyClient.headOfficeAddress?.streetName ?? "",
					city: currentUserSocietyClient.headOfficeAddress?.city ?? "",
					postalCode:
						currentUserSocietyClient.headOfficeAddress?.postalCode ?? "",
				});
			}
		} else if (actionsValue.buildingType === "PRIVATE_WORK") {
			setSelectedClient(undefined);
			setClientValues("clientUsers", [
				{
					id: undefined,
					firstname: "",
					lastname: "",
					email: "",
					streetNumber: "",
					streetName: "",
					postalCode: "",
					city: "",
				},
			]);
		}
	}, [
		activeStepIndex,
		currentUserIsSyndic,
		actionsValue.buildingType,
		setClientValues,
		setDetailsValues,
		getCurrentUserSociety,
	]);

	// ===== MUTATIONS =====
	const queryClient = useQueryClient();
	const { mutate: createConstructionSiteClientMutation } = useMutation({
		mutationFn: createConstructionSiteClient,
		onSuccess: async (data) => {
			toast("Le client a bien été créé");
			if (data.constructionSiteId) {
				await queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.detail(
						data.constructionSiteId.toString(),
					),
				});
				if (projectType === "gfa") {
					navigate(
						PAGES.GFA.edit.withParams(data.constructionSiteId.toString(), 2),
					);
				} else {
					navigate(
						PAGES.PROJECTS.edit.withParams(
							data.constructionSiteId.toString(),
							2,
						),
					);
				}
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la création du client", {
				description: errorMessage,
			});
		},
	});

	const { mutate: validateConstructionSiteMutation } =
		useValidateConstructionSiteDetails();
	const { mutate: saveConstructionSiteMutation } =
		useSaveConstructionSiteDetails();

	// GFA-specific mutations
	const { mutate: saveGFAMutation } = useMutation({
		mutationFn: (
			params: Parameters<typeof updateConstructionSiteDetails>[0] & {
				isNext?: boolean;
				step?: number;
			},
		) => {
			const { isNext: _isNext, step: _step, ...mutationParams } = params;

			return updateConstructionSiteDetails(mutationParams);
		},
		onSuccess: (data, variables) => {
			toast("Les opérations ont bien été sauvegardées");
			const { isNext, step } = variables;

			if (isNext) {
				navigate(
					PAGES.GFA.edit.withParams(data.id?.toString() ?? "", step ?? 3),
				);
			} else {
				navigate(
					PAGES.GFA.show.withParams(data.id?.toString() ?? "", "operation"),
				);
			}
		},
		onError: (error: AxiosError<{ message?: string }>) => {
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"Echec lors de la modification des opérations";
			toast.error(errorMessage);
			console.error("Construction site creation error:", error);
		},
	});

	const { mutate: saveGFAFinancingsMutation } = useSaveGFAFinancings();
	const { mutate: saveGFAShareholdersAndExperiencesMutation } =
		useSaveGFAShareholdersAndExperiences();
	const { mutate: saveGFAAutorisationsAndReferencesMutation } =
		useSaveGFAAutorisationsAndReferences();

	// ===== EVENT HANDLERS =====
	const onSubmitClient = useCallback(
		async (data: CreateConstructionSiteClientFormData) => {
			createConstructionSiteClientMutation({
				...data,
				isGFA: projectType === "gfa",
				...(selectedBrokerSociety && {
					brokerSocietyId: Number(selectedBrokerSociety.id),
				}),
				...(currentProject?.id && {
					constructionSiteId: currentProject.id,
				}),
				...(currentUserIsSyndic &&
					actionsValue?.buildingType && {
						actions: actionsValue,
					}),
			});
		},
		[
			createConstructionSiteClientMutation,
			selectedBrokerSociety,
			currentProject,
			projectType,
			currentUserIsSyndic,
			actionsValue,
		],
	);

	const onSaveDetails = useCallback(
		async (
			data: ConstructionSiteDetailsFormData & {
				isNext?: boolean;
				step?: number;
			},
		) => {
			try {
				if (
					!selectedClient ||
					!selectedMOAClient ||
					!getClientValues("clientIsMOA")
				) {
					toast.error("Veuillez sélectionner un client et un MOA");
					return;
				}

				const mutationData = {
					constructionSiteId: currentProject?.id,
					clientType: getClientValues("clientType"),
					clientIds:
						getClientValues("clientType") === "USER"
							? getClientValues("clientUsers")?.map((user) => Number(user.id))
							: getClientValues("clientSociety")?.id
								? [Number(getClientValues("clientSociety")?.id)]
								: undefined,
					clientIsMOA: getClientValues("clientIsMOA") === "true",
					moaClientType: getClientValues("moaClientType"),
					moaClientId: selectedMOAClient?.id,
					constructionSiteDetailsDto: data,
					action: "save" as const,
					isGFA: projectType === "gfa",
					...(selectedBrokerSociety && {
						brokerSocietyId: Number(selectedBrokerSociety.id),
					}),
				};

				if (projectType === "gfa") {
					saveGFAMutation({
						...mutationData,
						isNext: data.isNext,
						step: data.step,
					});
				} else {
					saveConstructionSiteMutation(mutationData);
					if (data.isNext && data.step) {
						navigate(
							PAGES.PROJECTS.edit.withParams(
								currentProject?.id?.toString() ?? "",
								data.step,
							),
						);
					} else {
						navigate(PAGES.PROJECTS.index);
					}
				}
			} catch (error) {
				console.error(error);
				if (axios.isAxiosError(error)) {
					toast.error(error.response?.data.message);
				}
			}
		},
		[
			selectedClient,
			selectedMOAClient,
			getClientValues,
			currentProject?.id,
			projectType,
			selectedBrokerSociety,
			saveGFAMutation,
			saveConstructionSiteMutation,
			navigate,
		],
	);

	const onSubmitDetails = useCallback(
		async (formData: ConstructionSiteDetailsFormData) => {
			const differences = getChangedFieldsObject(
				detailsDefaultValues,
				formData,
			);
			const hasChanges = Object.keys(differences).length > 0;
			try {
				if (
					!selectedClient ||
					!selectedMOAClient ||
					!getClientValues("clientIsMOA")
				)
					return;
				validateConstructionSiteMutation({
					constructionSiteId: currentProject?.id,
					clientType: getClientValues("clientType"),
					clientIds:
						getClientValues("clientType") === "USER"
							? getClientValues("clientUsers")?.map((user) => Number(user.id))
							: getClientValues("clientSociety")?.id
								? [Number(getClientValues("clientSociety")?.id)]
								: undefined,
					clientIsMOA: getClientValues("clientIsMOA") === "true",
					moaClientType: getClientValues("moaClientType"),
					moaClientId: selectedMOAClient?.id,
					constructionSiteDetailsDto: formData,
					action: "validate",
					hasChanges,
					...(selectedBrokerSociety && {
						brokerSocietyId: Number(selectedBrokerSociety.id),
					}),
				});
			} catch (error) {
				console.error(error);
				if (axios.isAxiosError(error)) {
					toast.error(error.response?.data.message);
				}
			}
		},
		[
			detailsDefaultValues,
			selectedClient,
			selectedMOAClient,
			getClientValues,
			currentProject?.id,
			validateConstructionSiteMutation,
			selectedBrokerSociety,
		],
	);

	// GFA-specific handlers
	const onSubmitFinancings = useCallback(
		async (
			formData: GFAFinancingsFormData & { isNext?: boolean; step?: number },
		) => {
			try {
				const { isNext, step, ...formDataWithoutIsNextAndStep } = formData;
				const differences = getChangedFieldsObject(
					financingsDefaultValues,
					formDataWithoutIsNextAndStep,
				);
				if (
					Object.keys(differences).length > 0 ||
					!GFAFullDetails?.gfaFinancings
				) {
					saveGFAFinancingsMutation({
						gfaId: Number(projectId),
						data: formDataWithoutIsNextAndStep,
					});
				}
				if (isNext) {
					setActiveStepIndex(step ?? 4);
				} else {
					navigate(
						PAGES.GFA.show.withParams(projectId?.toString() ?? "", "financing"),
					);
				}
			} catch (error) {
				console.error(error);
			}
		},
		[
			financingsDefaultValues,
			GFAFullDetails?.gfaFinancings,
			saveGFAFinancingsMutation,
			projectId,
			navigate,
			setActiveStepIndex,
		],
	);

	const onSubmitShareholdersAndExperiences = useCallback(
		async (
			formData: GFAShareholdersAndExperiencesFormData & {
				isNext?: boolean;
				step?: number;
			},
		) => {
			try {
				const { isNext, step, ...formDataWithoutIsNextAndStep } = formData;
				const differences = getChangedFieldsObject(
					shareholdersAndExperiencesDefaultValues,
					formDataWithoutIsNextAndStep,
				);
				if (
					Object.keys(differences).length > 0 ||
					!GFAFullDetails?.gfaShareholdersAndExperiences
				) {
					saveGFAShareholdersAndExperiencesMutation({
						gfaId: Number(projectId),
						data: formDataWithoutIsNextAndStep,
					});
				}
				if (isNext) {
					setActiveStepIndex(step ?? 5);
				} else {
					navigate(
						PAGES.GFA.show.withParams(projectId?.toString() ?? "", "operator"),
					);
				}
			} catch (error) {
				console.error(error);
			}
		},
		[
			shareholdersAndExperiencesDefaultValues,
			GFAFullDetails?.gfaShareholdersAndExperiences,
			saveGFAShareholdersAndExperiencesMutation,
			projectId,
			navigate,
			setActiveStepIndex,
		],
	);

	const onSubmitAutorisationsAndReferences = useCallback(
		async (
			formData: GFAAutorisationsAndReferencesFormData & {
				isNext?: boolean;
				step?: number;
			},
		) => {
			try {
				const { isNext, step, ...formDataWithoutIsNextAndStep } = formData;
				const differences = getChangedFieldsObject(
					autorisationsAndReferencesDefaultValues,
					formDataWithoutIsNextAndStep,
				);
				if (
					Object.keys(differences).length > 0 ||
					!GFAFullDetails?.gfaAutorisationsAndReferences
				) {
					saveGFAAutorisationsAndReferencesMutation({
						gfaId: Number(projectId),
						data: formDataWithoutIsNextAndStep,
					});
				}
				if (isNext) {
					setActiveStepIndex(step ?? 6);
				} else {
					navigate(
						PAGES.GFA.show.withParams(projectId?.toString() ?? "", "operation"),
					);
				}
			} catch (error) {
				console.error(error);
			}
		},
		[
			autorisationsAndReferencesDefaultValues,
			GFAFullDetails?.gfaAutorisationsAndReferences,
			saveGFAAutorisationsAndReferencesMutation,
			projectId,
			navigate,
			setActiveStepIndex,
		],
	);

	// ===== STEP NAVIGATION LOGIC =====
	const saveCurrentStep = useCallback(
		(nextStep: number) => {
			const detailsDataToSave = detailsForm.getValues();
			switch (activeStepIndex) {
				case 1:
					handleSubmitClient(onSubmitClient)();
					break;
				case 2:
					onSaveDetails({
						...detailsDataToSave,
						isNext: true,
						step: nextStep,
					});
					break;
				case 3:
					onSubmitFinancings({
						...financingsForm.getValues(),
						isNext: true,
						step: nextStep,
					});
					break;
				case 4:
					onSubmitShareholdersAndExperiences({
						...shareholdersAndExperiencesForm.getValues(),
						isNext: true,
						step: nextStep,
					});
					break;
				case 5:
					onSubmitAutorisationsAndReferences({
						...autorisationsAndReferencesForm.getValues(),
						isNext: true,
						step: nextStep,
					});
					break;
				case 6:
					setActiveStepIndex(nextStep);
					break;
				default:
					setActiveStepIndex(nextStep);
					break;
			}
		},
		[
			detailsForm,
			activeStepIndex,
			handleSubmitClient,
			onSubmitClient,
			onSaveDetails,
			onSubmitFinancings,
			financingsForm,
			onSubmitShareholdersAndExperiences,
			shareholdersAndExperiencesForm,
			onSubmitAutorisationsAndReferences,
			autorisationsAndReferencesForm,
			setActiveStepIndex,
		],
	);

	const setCurrentStepIndex = (stepIndex: number) => {
		if (stepIndex === 1) {
			saveCurrentStep(stepIndex);
		}
		if (stepIndex === 2 && selectedClient) {
			saveCurrentStep(stepIndex);
		}
		if (stepIndex === 3) {
			if (projectType === "construction-site") {
				onSubmitDetails(detailsForm.getValues());
			} else {
				saveCurrentStep(stepIndex);
			}
		}
		if (
			stepIndex === 4 &&
			(GFAFullDetails?.gfaFinancings || activeStepIndex === 3)
		) {
			saveCurrentStep(stepIndex);
		}
		if (
			stepIndex === 5 &&
			(GFAFullDetails?.gfaShareholdersAndExperiences || activeStepIndex === 4)
		) {
			saveCurrentStep(stepIndex);
		}
		if (
			stepIndex === 6 &&
			(GFAFullDetails?.gfaAutorisationsAndReferences || activeStepIndex === 5)
		) {
			saveCurrentStep(stepIndex);
		}
	};

	return (
		<SelectedBrokerSocietyProvider
			selectedBrokerSociety={selectedBrokerSociety}
		>
			<BaseProjectForm
				pageTitle={pageTitle}
				isCurrentUserSyndic={currentUserIsSyndic}
				onBack={() => navigate(PAGES.PROJECTS.index)}
				projectType={projectType}
				stepsData={{
					steps: stepperSteps,
					activeStepIndex: activeStepIndex,
					setCurrentStepIndex,
				}}
				asBrokerData={{
					brokersSocieties: brokersSocieties?.data ?? [],
					canSelectBrokerSociety,
					brokerSocietyOptions,
					selectedBrokerSociety: selectedBrokerSociety ?? null,
					setSelectedBrokerSociety,
					setSearchBroker,
					disabled:
						activeStepIndex !== 1 ||
						(currentProject && !!selectedBrokerSociety) ||
						currentUserIsSyndic,
				}}
				clientSelectData={{
					form: clientForm,
					selectedClient,
					setSelectedClient,
					initialClientSocietyId,
					actionsValue,
					canValidate: canSelectBrokerSociety ? !!selectedBrokerSociety : true,
					excludeSyndics: projectType === "gfa",
					handleSubmitClient,
					onSubmitClient,
				}}
				detailsData={{
					canSave,
					currentUserIsClient,
					clientIsSyndic,
					form: detailsForm,
					handleSubmit: handleSubmitDetails,
					onSubmit: onSubmitDetails,
					onSave: onSaveDetails,
				}}
				GFAData={
					GFAFullDetails
						? {
								GFAFullDetails,
								financings: {
									form: financingsForm,
									onSubmit: onSubmitFinancings,
								},
								shareholdersAndExperiences: {
									form: shareholdersAndExperiencesForm,
									onSubmit: onSubmitShareholdersAndExperiences,
								},
								authorisationsAndReferences: {
									form: autorisationsAndReferencesForm,
									onSubmit: onSubmitAutorisationsAndReferences,
								},
							}
						: undefined
				}
			/>
		</SelectedBrokerSocietyProvider>
	);
};
